
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface ProfileData {
  profile: any;
  education: any[];
  experience: any[];
  skills: any[];
  projects: any[];
  loading: boolean;
  githubUrl?: string | null;
  linkedinUrl?: string | null;
}

export const useProfileData = (userId: string) => {
  const [data, setData] = useState<ProfileData>({
    profile: null,
    education: [],
    experience: [],
    skills: [],
    projects: [],
    loading: true,
    githubUrl: null,
    linkedinUrl: null,
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userId) return;

      try {
        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
          .from('job_seeker_profiles')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (profileError) throw profileError;

        // Fetch education data
        const { data: educationData, error: educationError } = await supabase
          .from('education')
          .select('*')
          .eq('job_seeker_id', userId)
          .order('is_current', { ascending: false })
          .order('end_date', { ascending: false })
          .limit(1);
        
        if (educationError) throw educationError;

        // Fetch experience data
        const { data: experienceData, error: experienceError } = await supabase
          .from('work_experience')
          .select('*')
          .eq('job_seeker_id', userId)
          .order('is_current', { ascending: false })
          .order('end_date', { ascending: false })
          .limit(1);
        
        if (experienceError) throw experienceError;

        // Fetch skills data
        const { data: skillsData, error: skillsError } = await supabase
          .from('job_seeker_skills')
          .select(`
            id,
            proficiency_level,
            years_of_experience,
            skill_name
          `)
          .eq('job_seeker_id', userId);
        
        if (skillsError) throw skillsError;

        // Fetch projects data
        const { data: projectsData, error: projectsError } = await supabase
          .from('job_seeker_projects')
          .select(`
            id,
            title,
            description,
            project_url,
            project_skills (skill_name)
          `)
          .eq('job_seeker_id', userId);
        
        if (projectsError) throw projectsError;
        
        const formattedProjects = (projectsData || []).map(project => ({
          id: project.id,
          title: project.title,
          description: project.description,
          project_url: project.project_url,
          skills: project.project_skills.map((skill: any) => skill.skill_name)
        }));

        setData({
          profile: profileData,
          education: educationData || [],
          experience: experienceData || [],
          skills: skillsData || [],
          projects: formattedProjects,
          loading: false,
          githubUrl: profileData?.github_url || null,
          linkedinUrl: profileData?.linkedin_url || null,
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchProfileData();
  }, [userId]);

  return data;
};
